// This Modal displays all volunteers allocated in an area

import React, { useEffect, useState, useCallback } from "react";
import { Area, Event, VolunteerWithAllocation, Allocation, VolunteerFiltersWithConflictProps, BaseFilterUpdate, Flag, CoreTeam } from "../data/types";
import { API_BASE_URL, LoadingSpinnerMeta, VolunteerAreaShiftViewTableMeta, AllLanguages, IVPositionFilterOptions, GenderOptions, ALLOCATED_DEFAULT_FILTERS, VolunteerAllocationViewTableMeta, AVAILABILITY_OPTIONS, UPLOAD_DOCUMENT_TYPES } from "../data";
import { Button } from "./ui/Button";
import { Modal } from "./ui/Modal";
import VolunteerTable from "./VolunteerTable"
import { VolunteerShiftViewModal } from "./VolunteerShiftViewModal";
import { LoadingSpinner } from "./LoadingSpinner";
import { VolunteerFilters } from "./VolunteerFilters";
import { ChevronLeftIcon, ChevronRightIcon, DownloadIcon } from "../icons";
import { useAuth } from "../routes/auth/AuthContext";
import { VolunteerProfileModal } from "./VolunteerProfileModal";

const ITEMS_PER_PAGE = 25;

type PaginationData = {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
};

type Props = {
    meta: any;
    area: Area;
    isOpen: boolean;
    onCancel: () => void;
    title: string;
    message: string;
};

export const VolunteerAreaModal = ({
    meta,
    area,
    isOpen,
    onCancel,
    title,
    message,
}: Props) => {
    const { user } = useAuth();

    const [error, setError] = useState<string | null>(null);
    const [volunteers, setVolunteers] = useState<VolunteerWithAllocation[]>([]);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);

    // Pagination State
    const [pagination, setPagination] = useState<PaginationData>({
        current_page: 1,
        last_page: 1,
        per_page: ITEMS_PER_PAGE,
        total: 0
    });

    // Modal & Selection State
    const [isViewShiftModalOpen, setIsViewShiftModalOpen] = useState(false);
    const [selectedAllocations, setSelectedAllocations] = useState<Allocation[]>([]);
    const [selectedCoreTeams, setSelectedCoreTeams] = useState<CoreTeam[]>([]);
    const [selectedVolunteer, setSelectedVolunteer] = useState<VolunteerWithAllocation | null>(null);
    const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);

    // Filtering State
    const [flags, setFlags] = useState<Flag[]>([]);
    const [events, setEvents] = useState<Event[]>([]);
    const [appliedFilters, setAppliedFilters] = useState<VolunteerFiltersWithConflictProps>(() => ({ ...ALLOCATED_DEFAULT_FILTERS }));

    // Build filter params (reusable for both pagination and export)
    const buildFilterParams = useCallback((filters: VolunteerFiltersWithConflictProps): URLSearchParams => {
        const params = new URLSearchParams();

        // Add all filter parameters
        if (filters.name) params.append('name', filters.name);
        if (filters.email) params.append('email', filters.email);
        if (filters.phone_number) params.append('phone', filters.phone_number);
        if (filters.id) params.append('id', filters.id);
        if (filters.country) params.append('country', filters.country);
        if (filters.country_of_birth) params.append('country_of_birth', filters.country_of_birth);
        if (filters.family_origin) params.append('family_origin', filters.family_origin);
        if (filters.jamat_khana) params.append('jamat_khana', filters.jamat_khana);
        if (filters.iv_position) params.append('iv_position', filters.iv_position);
        if (filters.ismaili !== null) params.append('ismaili', String(filters.ismaili));
        if (filters.registered_volunteer !== null) params.append('registered_volunteer', String(filters.registered_volunteer));
        if (filters.approved_volunteer !== null) params.append('approved_volunteer', String(filters.approved_volunteer));
        if (filters.eligible_for_duty !== null) params.append('eligible_for_duty', String(filters.eligible_for_duty));
        if (filters.multi_faith_family !== null) params.append('multi_faith_family', String(filters.multi_faith_family));
        if (filters.has_support_needs !== null) params.append('has_support_needs', String(filters.has_support_needs));
        if (filters.age_start !== null) params.append('age_start', String(filters.age_start));
        if (filters.age_end !== null) params.append('age_end', String(filters.age_end));
        if (filters.genders && filters.genders.length > 0) params.append('genders', JSON.stringify(filters.genders));
        if (filters.service_record && filters.service_record.length > 0) params.append('service_record', JSON.stringify(filters.service_record));
        if (filters.languages && filters.languages.length > 0) params.append('languages', JSON.stringify(filters.languages));
        if (filters.tags && filters.tags.length > 0) params.append('tags', JSON.stringify(filters.tags.map(t => t.id)));
        if (filters.trainings && filters.trainings.length > 0) params.append('trainings', JSON.stringify(filters.trainings.map(t => t.id)));
        if (filters.training_filter_type) params.append('training_filter_type', filters.training_filter_type);
        if (filters.availability && filters.availability.length > 0) {
            params.append('availability', JSON.stringify(filters.availability));
            if (filters.availability_filter_type) params.append('availability_filter_type', filters.availability_filter_type);
        }
        if (filters.iiuk_registeration !== null) params.append('iiuk_registeration', String(filters.iiuk_registeration));
        if (filters.volunteer_for_visit !== null) params.append('volunteer_for_visit', String(filters.volunteer_for_visit));
        if (filters.event_assignment_type && filters.event_assignment_events && filters.event_assignment_events.length > 0) {
            params.append('event_assignment_type', filters.event_assignment_type);
            params.append('event_assignment_events', JSON.stringify(filters.event_assignment_events.map(e => e.id)));
        }
        if (filters.core_team_assignment_type) params.append('core_team_assignment_type', filters.core_team_assignment_type);

        // Status filter (allocation acceptance status)
        if (filters.status !== null) params.append('status', String(filters.status));

        if (filters.documents && filters.documents.length > 0) {
            params.append('documents', JSON.stringify(filters.documents))
        }

        return params;
    }, []);

    // Build query string for paginated API calls
    const buildQueryString = useCallback((page: number, filters: VolunteerFiltersWithConflictProps) => {
        const params = buildFilterParams(filters);
        params.append('per_page', ITEMS_PER_PAGE.toString());
        params.append('page', page.toString());
        return params.toString();
    }, [buildFilterParams]);

    // Normalize volunteer data from API response
    const normalizeVolunteer = (v: any): VolunteerWithAllocation => ({
        ...v,
        eligible_for_duty: v.eligible_for_duty === 1 || v.eligible_for_duty === true,
        ismaili: v.ismaili === 1 || v.ismaili === true,
        registered_volunteer: v.registered_volunteer === 1 || v.registered_volunteer === true,
        multi_faith_family: v.multi_faith_family === 1 || v.multi_faith_family === true,
        volunteer_for_visit: v.volunteer_for_visit !== null && v.volunteer_for_visit !== undefined ? Number(v.volunteer_for_visit) : v.volunteer_for_visit,
        // Map Leadership Roles (snake_case -> camelCase)
        eventLeads: v.event_leads || [],
        areasAsLead: v.areas_as_lead || [],
        areasAsDeputyLead: v.areas_as_deputy_lead || [],
        shiftsAsLead: v.shifts_as_lead || [],
        shiftsAsDeputyLead: v.shifts_as_deputy_lead || [],
        // Map Core Teams
        coreTeams: v.core_teams || [],
    });

    // Fetch volunteers with pagination and filters
    const fetchVolunteers = useCallback(async (page: number, filters: VolunteerFiltersWithConflictProps) => {
        if (!area) return;

        setLoading(true);
        setError(null);

        try {
            const queryString = buildQueryString(page, filters);
            const response = await fetch(`${API_BASE_URL}/allocation/area/${area.id}?${queryString}`, { credentials: 'include' });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();
            const volunteersData = result.data || result;

            const normalized = volunteersData.map(normalizeVolunteer);

            setVolunteers(normalized);
            setPagination({
                current_page: result.current_page || 1,
                last_page: result.last_page || 1,
                per_page: result.per_page || ITEMS_PER_PAGE,
                total: result.total || 0
            });
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to fetch volunteer information.");
        } finally {
            setLoading(false);
        }
    }, [area, buildQueryString]);

    // Fetch additional filter data (flags, events)
    const fetchAdditionalData = useCallback(async () => {
        try {
            const [flagRes, eventRes] = await Promise.all([
                fetch(`${API_BASE_URL}/flag`, { credentials: 'include' }),
                fetch(`${API_BASE_URL}/event`, { credentials: 'include' })
            ]);

            if (!flagRes.ok) throw new Error("Failed to fetch flags");
            if (!eventRes.ok) throw new Error("Failed to fetch events");

            const [flagsData, eventsData] = await Promise.all([
                flagRes.json(),
                eventRes.json()
            ]);

            setFlags(flagsData);

            let eventsList = eventsData.value || eventsData;
            if (Array.isArray(eventsList) && eventsList.length > 0 && Array.isArray(eventsList[0])) {
                eventsList = eventsList[0];
            }
            setEvents(eventsList);
        } catch (err) {
            console.error("Failed to fetch additional data:", err);
        }
    }, []);

    // Initial data fetch when modal opens
    useEffect(() => {
        if (!isOpen || !area) return;

        fetchAdditionalData();
        fetchVolunteers(1, appliedFilters);
    }, [isOpen, area]);

    // Reset state when modal closes
    useEffect(() => {
        if (!isOpen) {
            setVolunteers([]);
            setError(null);
            setAppliedFilters({ ...ALLOCATED_DEFAULT_FILTERS });
            setPagination({
                current_page: 1,
                last_page: 1,
                per_page: ITEMS_PER_PAGE,
                total: 0
            });
        }
    }, [isOpen]);

    // Handle filter changes - refetch from page 1
    const handleFiltersChange = (update: BaseFilterUpdate) => {
        const newFilters = { ...appliedFilters, ...update };
        setAppliedFilters(newFilters);
        fetchVolunteers(1, newFilters);
    };

    // Handle page change
    const handlePageChange = (page: number) => {
        if (page >= 1 && page <= pagination.last_page) {
            fetchVolunteers(page, appliedFilters);
        }
    };

    // Injects Lead Rows into the Allocation List so the Modal can separate them
    const handleViewShiftClick = (allocations: Allocation[], volunteer?: VolunteerWithAllocation) => {
        // Filter allocations to only those in this area
        let combinedAllocations = [...(allocations || [])].filter(a => a.shift?.area_id === area.id);

        if (volunteer) {
            // Inject Area Lead Rows (only for this area)
            volunteer.areasAsLead?.filter(a => a.id === area.id).forEach(areaItem => {
                combinedAllocations.push({
                    id: -1,
                    volunteer_id: volunteer.csid,
                    shift_id: -1,
                    shift: {
                        id: -1,
                        title: "All Shifts",
                        duty: { name: "Area Lead" },
                        area: { name: areaItem.name, event: { name: areaItem.event?.name, include_in_duplicate_checks: areaItem.event?.include_in_duplicate_checks } }
                    },
                    allocation_times: [{ status: true, start_time: null, end_time: null }]
                } as any);
            });

            volunteer.areasAsDeputyLead?.filter(a => a.id === area.id).forEach(areaItem => {
                combinedAllocations.push({
                    id: -1,
                    volunteer_id: volunteer.csid,
                    shift_id: -1,
                    shift: {
                        id: -1,
                        title: "All Shifts",
                        duty: { name: "Area Deputy Lead" },
                        area: { name: areaItem.name, event: { name: areaItem.event?.name, include_in_duplicate_checks: areaItem.event?.include_in_duplicate_checks } }
                    },
                    allocation_times: [{ status: true, start_time: null, end_time: null }]
                } as any);
            });

            // Inject Shift Lead Rows (only for shifts in this area)
            volunteer.shiftsAsLead?.filter(s => s.area_id === area.id).forEach(shift => {
                combinedAllocations.push({
                    id: -1,
                    volunteer_id: volunteer.csid,
                    shift_id: -1,
                    shift: {
                        id: -1,
                        title: shift.title || shift.duty?.name || 'Shift',
                        duty: { name: "Shift Lead" },
                        area: { name: shift.area?.name || area.name, event: { name: shift.area?.event?.name, include_in_duplicate_checks: shift.area?.event?.include_in_duplicate_checks } }
                    },
                    allocation_times: [{ status: true, start_time: null, end_time: null }]
                } as any);
            });

            volunteer.shiftsAsDeputyLead?.filter(s => s.area_id === area.id).forEach(shift => {
                combinedAllocations.push({
                    id: -1,
                    volunteer_id: volunteer.csid,
                    shift_id: -1,
                    shift: {
                        id: -1,
                        title: shift.title || shift.duty?.name || 'Shift',
                        duty: { name: "Shift Deputy Lead" },
                        area: { name: shift.area?.name || area.name, event: { name: shift.area?.event?.name, include_in_duplicate_checks: shift.area?.event?.include_in_duplicate_checks } }
                    },
                    allocation_times: [{ status: true, start_time: null, end_time: null }]
                } as any);
            });
        }

        setSelectedAllocations(combinedAllocations);
        setSelectedCoreTeams(volunteer?.coreTeams || []);
        setIsViewShiftModalOpen(true);
    };

    const handleViewShiftClickClose = () => {
        setSelectedAllocations([]);
        setSelectedCoreTeams([]);
        setIsViewShiftModalOpen(false);
    };

    // Export CSV - fetches all data using export endpoint
    const handleExportCSV = async () => {
        setExporting(true);
        try {
            // Build query string with filters (no pagination for export)
            const params = buildFilterParams(appliedFilters);

            const response = await fetch(`${API_BASE_URL}/allocation/area/${area.id}/export?${params.toString()}`, { credentials: 'include' });

            if (!response.ok) {
                throw new Error(`Failed to fetch export data: ${response.status}`);
            }

            const dataToExport = (await response.json()).map(normalizeVolunteer);

            // Generate CSV
            const headers = [
                'ID', 'First Name', 'Last Name', 'Date of Birth', 'Email', 'Phone',
                'Age', 'Age Band', 'Gender', 'Jamatkhana', 'Additional Skills',
                'Ismaili', 'Inter-Faith Family', 'Family Origin', 'Languages',
                'Service Record', 'Support Needs', 'Shift Count', 'Shifts'
            ];

            const rows = dataToExport.map((item: VolunteerWithAllocation) => {
                const roles: string[] = [];

                item.eventLeads?.filter(e => e.include_in_duplicate_checks !== false).forEach(e => {
                    const role = (e as any).pivot?.role || "Event Lead";
                    roles.push(`${e.name}: All Areas: All Shifts [${role}]`);
                });

                item.areasAsLead?.filter(a => a.event?.include_in_duplicate_checks !== false).forEach(a => {
                    const eventName = a.event?.name || 'Unknown Event';
                    roles.push(`${eventName}: ${a.name}: All Shifts [Area Lead]`);
                });

                item.areasAsDeputyLead?.filter(a => a.event?.include_in_duplicate_checks !== false).forEach(a => {
                    const eventName = a.event?.name || 'Unknown Event';
                    roles.push(`${eventName}: ${a.name}: All Shifts [Deputy Area Lead]`);
                });

                item.shiftsAsLead?.filter(s => s.area?.event?.include_in_duplicate_checks !== false).forEach(s => {
                    const eventName = s.area?.event?.name || 'Unknown Event';
                    const areaName = s.area?.name || 'Unknown Area';
                    const shiftName = s.title || s.duty?.name || 'Unknown Shift';
                    roles.push(`${eventName}: ${areaName}: ${shiftName} [Shift Lead]`);
                });

                item.shiftsAsDeputyLead?.filter(s => s.area?.event?.include_in_duplicate_checks !== false).forEach(s => {
                    const eventName = s.area?.event?.name || 'Unknown Event';
                    const areaName = s.area?.name || 'Unknown Area';
                    const shiftName = s.title || s.duty?.name || 'Unknown Shift';
                    roles.push(`${eventName}: ${areaName}: ${shiftName} [Deputy Shift Lead]`);
                });

                const standardShifts = item.allocations?.filter(a => a.shift?.area?.event?.include_in_duplicate_checks !== false).map(a => {
                    const eventName = a.shift?.area?.event?.name || 'Unknown Event';
                    const areaName = a.shift?.area?.name || 'Unknown Area';
                    const shift = a.shift?.title || a.shift?.duty?.name || 'Unknown Shift';

                    const latestTime = a.allocation_times?.sort((x: any, y: any) =>
                        new Date(y.created_at).getTime() - new Date(x.created_at).getTime()
                    )[0];

                    const statusRaw = latestTime?.status;
                    let statusStr = "Pending";
                    if (statusRaw === true) statusStr = "Accepted";
                    else if (statusRaw === false) statusStr = "Declined";

                    return `${eventName}: ${areaName}: ${shift} : ${statusStr}`;
                }) || [];

                const allActivity = [...roles, ...standardShifts].join(' | ');

                return [
                    item.csid ? `="${item.csid}"` : '',
                    item.first_name || '',
                    item.last_name || '',
                    item.dob || '',
                    item.email || '',
                    item.phone ? `="${item.phone}"` : '',
                    item.age || '',
                    item.age_band || '',
                    item.gender || '',
                    item.jamat_khana || '',
                    (item.flags || []).map(f => f.name).join(', ') || '',
                    item.ismaili ? 'Yes' : 'No',
                    item.multi_faith_family ? 'Yes' : 'No',
                    item.family_origin || '',
                    item.languages || '',
                    item.service_record || '',
                    item.support_needs || '',
                    (roles.length + standardShifts.length).toString(),
                    allActivity || ''
                ];
            });

            const csvRows = [headers];
            rows.forEach(row => {
                const processedRow = row.map(cell => {
                    let cellStr = String(cell);
                    cellStr = cellStr.replace(/[\r\n]+/g, ' ').trim();
                    if (cellStr.startsWith('=')) return cellStr;
                    if (cellStr.includes(',') || cellStr.includes('"')) {
                        return `"${cellStr.replace(/"/g, '""')}"`;
                    }
                    return cellStr;
                });
                csvRows.push(processedRow);
            });

            const csvContent = csvRows.map(row => row.join(',')).join('\n');
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            const date = new Date().toISOString().split('T')[0];
            const areaName = area.name.replace(/[^a-z0-9]/gi, '_').toLowerCase();

            link.setAttribute('href', url);
            link.setAttribute('download', `area_${areaName}_volunteers_${date}.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (err) {
            console.error('Export failed:', err);
            setError(err instanceof Error ? err.message : 'Failed to export data');
        } finally {
            setExporting(false);
        }
    };

    const handleAllocationRemoved = (allocationId: number, volunteerId: number) => {
        setVolunteers(prev => {
            const updated = prev.map(volunteer => {
                if (volunteer.csid === volunteerId) {
                    const updatedAllocations = volunteer.allocations?.filter(a => a.id !== allocationId) ?? [];
                    return { ...volunteer, allocations: updatedAllocations };
                }
                return volunteer;
            });
            // Remove volunteers with no remaining allocations in this area
            return updated.filter(volunteer =>
                volunteer.allocations?.some(a => a.shift?.area_id === area.id) ||
                volunteer.areasAsLead?.some(a => a.id === area.id) ||
                volunteer.areasAsDeputyLead?.some(a => a.id === area.id) ||
                volunteer.shiftsAsLead?.some(s => s.area_id === area.id) ||
                volunteer.shiftsAsDeputyLead?.some(s => s.area_id === area.id)
            );
        });
        setSelectedAllocations(prev => prev.filter(a => a.id !== allocationId));
    };

    const handleProfileClick = (volunteer: VolunteerWithAllocation) => {
        setSelectedVolunteer(volunteer);
        setIsProfileModalOpen(true);
    };

    const handleProfileClickClose = () => {
        setSelectedVolunteer(null);
        setIsProfileModalOpen(false);
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onCancel}
            title={title}
            showCloseButton={true}
            maxWidth="7xl"
            footer={
                <div className="flex justify-end">
                    <Button onClick={onCancel} variant="outline" color="neutral">
                        Close
                    </Button>
                </div>
            }
        >
            {error && !loading && (
                <div className="m-6 bg-danger-100 dark:bg-danger-900 border border-danger-400 dark:border-danger-700 text-danger-700 dark:text-danger-200 px-4 py-3 rounded">
                    {error}
                </div>
            )}

            <>
                <div className="px-6 py-3">
                    <p className="text-md text-gray-500 dark:text-gray-400">
                        {message}
                    </p>
                </div>

                <div className="p-4 mt-4">
                    <VolunteerFilters
                        initialFilters={appliedFilters}
                        defaultFilters={ALLOCATED_DEFAULT_FILTERS}
                        onChange={handleFiltersChange}
                        tagOptions={flags}
                        languageOptions={AllLanguages}
                        serviceRecordOptions={IVPositionFilterOptions}
                        genderOptions={GenderOptions}
                        availabilityOptions={AVAILABILITY_OPTIONS as unknown as string[]}
                        documentOptions={UPLOAD_DOCUMENT_TYPES}
                        showConflict={false}
                        showStatus={true}
                        showRegisteredVolunteer={true}
                        eventOptions={events}
                    />
                    <div className="flex items-center justify-between mb-4">
                        <p className="text-md text-gray-500 dark:text-gray-400">
                            {`Volunteer Count: ${pagination.total}`}
                        </p>
                        {user?.role === 'admin' && (
                            <button
                                onClick={handleExportCSV}
                                disabled={pagination.total === 0 || exporting}
                                className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <DownloadIcon className="w-4 h-4" />
                                {exporting ? 'Exporting...' : 'Export to CSV'}
                            </button>
                        )}
                    </div>

                    {loading ? (
                        <div className="m-6">
                            <LoadingSpinner meta={LoadingSpinnerMeta} />
                        </div>
                    ) : (
                        <VolunteerTable
                            meta={VolunteerAllocationViewTableMeta}
                            data={volunteers}
                            training={true}
                            status={false}
                            allocation={true}
                            conflicts={false}
                            select={false}
                            disablePagination={true}
                            viewShiftsClick={handleViewShiftClick}
                            onProfileClick={handleProfileClick}
                        />
                    )}

                    {/* Pagination Controls */}
                    {pagination.last_page > 1 && (
                        <div className="flex items-center justify-between mt-4">
                            <div className="flex items-center gap-2">
                                <Button
                                    type="button"
                                    onClick={() => handlePageChange(1)}
                                    disabled={pagination.current_page <= 1 || loading}
                                    size="sm"
                                >
                                    <ChevronLeftIcon /><ChevronLeftIcon className="w-5 h-5 -ms-[22px]" /> First
                                </Button>

                                <Button
                                    type="button"
                                    onClick={() => handlePageChange(pagination.current_page - 1)}
                                    disabled={pagination.current_page <= 1 || loading}
                                    size="sm"
                                >
                                    <ChevronLeftIcon /> Previous
                                </Button>

                                {(() => {
                                    const pageNumbers = [];
                                    const maxPagesToShow = 5;
                                    let startPage = Math.max(1, pagination.current_page - Math.floor(maxPagesToShow / 2));
                                    let endPage = Math.min(pagination.last_page, startPage + maxPagesToShow - 1);

                                    if (endPage - startPage + 1 < maxPagesToShow) {
                                        startPage = Math.max(1, endPage - maxPagesToShow + 1);
                                    }

                                    for (let i = startPage; i <= endPage; i++) {
                                        pageNumbers.push(
                                            <Button
                                                key={i}
                                                type="button"
                                                onClick={() => handlePageChange(i)}
                                                variant={i === pagination.current_page ? "solid" : "outline"}
                                                disabled={loading}
                                                size="sm"
                                            >
                                                {i}
                                            </Button>
                                        );
                                    }
                                    return pageNumbers;
                                })()}

                                <Button
                                    type="button"
                                    onClick={() => handlePageChange(pagination.current_page + 1)}
                                    disabled={pagination.current_page >= pagination.last_page || loading}
                                    size="sm"
                                >
                                    Next <ChevronRightIcon />
                                </Button>

                                <Button
                                    type="button"
                                    onClick={() => handlePageChange(pagination.last_page)}
                                    disabled={pagination.current_page >= pagination.last_page || loading}
                                    size="sm"
                                >
                                    Last <ChevronRightIcon /><ChevronRightIcon className="w-5 h-5 -ms-[22px]" />
                                </Button>
                            </div>

                            <span className="text-sm text-gray-600 dark:text-gray-400">
                                Page {pagination.current_page} of {pagination.last_page}
                            </span>
                        </div>
                    )}
                </div>
            </>

            <VolunteerShiftViewModal
                meta={VolunteerAreaShiftViewTableMeta}
                allocations={selectedAllocations}
                coreTeams={selectedCoreTeams}
                isOpen={isViewShiftModalOpen}
                onCancel={handleViewShiftClickClose}
                title="Volunteer Shifts"
                message="The volunteer has been allocated to following shifts: "
                onAllocationRemoved={handleAllocationRemoved}
            />

            <VolunteerProfileModal
                volunteerId={selectedVolunteer?.csid || null}
                isOpen={isProfileModalOpen}
                onClose={handleProfileClickClose}
            />
        </Modal>
    );
};
